import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  useNavigationGuard,
  useUnsavedChanges,
} from "../../../components/layout/UnsavedChanges";
import {
  Button,
  EmptyState,
  HintBanner,
  SetupBanner,
  SetupBannerSkeleton,
  SkeletonCard,
  Tabs,
  failedEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { sameRichText } from "../../../lib/richText";
import { BRAND_TABS, brandTabFromParam } from "../brandTabs";
import BrandDetailsForm from "../components/BrandDetailsForm";
import BrandDetailsFormSkeleton from "../components/BrandDetailsFormSkeleton";
import BrandConfigurationTab from "../components/BrandConfigurationTab";
import BrandFormTab from "../components/BrandFormTab";
import BrandOverview from "../components/BrandOverview";
import BrandPreviewPanel from "../components/BrandPreviewPanel";
import BrandPlansTab from "../components/BrandPlansTab";
import BrandPoliciesTab from "../components/BrandPoliciesTab";
import BrandServicesTab from "../components/BrandServicesTab";
import { emptyBrandDetails } from "../data/brandMock";
import { useBrandDetails } from "../hooks/useBrandDetails";
import { useBrandPolicies } from "../hooks/useBrandPolicies";
import { useBrandSave } from "../hooks/useBrandSave";
import {
  BrandDetails,
  BrandPreviewId,
  BrandTabId,
  PolicyDocumentId,
  PolicyDocuments,
  StoredPolicyDocuments,
} from "../types/brand.types";
import { isBrandSetUp } from "../setupProgress";
import { validateBrandDetails } from "../validation";

const POLICY_DOCUMENT_IDS: PolicyDocumentId[] = ["guidelines", "policy"];
/** How each document is named in a toast - "Guidelines saved". */
const POLICY_DOCUMENT_NAMES: Record<PolicyDocumentId, string> = {
  guidelines: "Guidelines",
  policy: "Policy",
};

const MyBrandPage: React.FC = () => {
  // In the URL rather than in state, so the tab survives a refresh and can be
  // linked to — the dashboard's "Create plans" step is what links to it, and
  // the same way the Settings screen carries its own.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = brandTabFromParam(searchParams.get("tab"));

  // A tab is left the way a rail item is left: handed to the shell, which
  // switches straight away where nothing is unsaved and asks first where
  // something is. The drafts do survive a switch — they are held on this page,
  // below — but a person who typed into the guidelines and pressed Details
  // has no way of knowing that, and the dialog is what tells them the typing
  // is still theirs to save or throw away. See `UnsavedChanges`.
  //
  // `replace`, so moving between tabs does not fill the back button with every
  // tab that was looked at along the way. The open tab pressed again is not a
  // switch, so it is not asked about either.
  const guardNavigation = useNavigationGuard();
  const setActiveTab = (tab: BrandTabId) => {
    if (tab === activeTab) return;
    guardNavigation(() => setSearchParams({ tab }, { replace: true }));
  };

  const [preview, setPreview] = useState<BrandPreviewId>("invoice");

  /**
   * Whether the form is open, as against the brand being read back.
   *
   * Seeded from the record when it lands — see below. A brand nobody has set
   * up opens on the form, because there is nothing to read back and filling it
   * in is the whole job; one that is already set up opens on `BrandOverview`,
   * with Edit as the way in. The same pair the Settings screen makes of a
   * profile, and for the same reasons — see `BrandOverview`.
   */
  const [editing, setEditing] = useState(false);

  // `details` is what the form and the preview both read. `saved` is the last
  // committed copy — what Cancel restores, and what Save compares against to
  // decide whether there is anything to send.
  const [details, setDetails] = useState<BrandDetails>(emptyBrandDetails);
  const [saved, setSaved] = useState<BrandDetails>(emptyBrandDetails);

  // Both records, fetched together as the screen opens.
  const { loaded, status, reload } = useBrandDetails();

  // Details that arrive quickly open the form straight away: the shape only
  // stands in once the fetch has visibly stalled.
  //
  // One phase for both columns, the way the dashboard drives all of its cards
  // from one: they wait together, shimmer together, and land together.
  const phase = useLoadPhase(status === "loading");
  const { saving, save } = useBrandSave();

  // What arrived is both what the form shows and what it counts as committed,
  // so the screen opens filled in and with nothing to save.
  //
  // Seeded during the render that first sees the load rather than in an effect
  // after it: an effect would paint the empty form once and fill it a frame
  // later, which reads as the screen overwriting itself. React re-runs this
  // render with the new state before committing either version of it.
  //
  // Once per load, and `loaded` is a fresh object only when a fetch answered —
  // so a retry seeds again while an ordinary re-render does not.
  const [seededFrom, setSeededFrom] = useState<BrandDetails | null>(null);
  if (loaded && loaded !== seededFrom) {
    setSeededFrom(loaded);
    setDetails(loaded);
    setSaved(loaded);

    // A brand nobody has set up is sent straight to the fields, and the strip
    // above them says why. There is no cancelling out of that: the form has no
    // Cancel until the brand is stored, and the render below keeps the fields
    // up while it is not — see both.
    setEditing(!isBrandSetUp(loaded));
  }

  const dirty = JSON.stringify(details) !== JSON.stringify(saved);
  const errors = validateBrandDetails(details);
  const valid = Object.keys(errors).length === 0;

  // The form's own way of marking up what is still missing, so the rail's
  // prompt can offer to save a form that cannot be saved yet and point at why.
  const problems = useRef<(() => void) | null>(null);

  // The same answer the Save row is armed from, told to the shell so the rail
  // and the tab strip ask before they take the form off screen. Whichever tab
  // is open: details typed and then left behind a switch to Plans are still
  // unsaved, and the screen leaving is what would lose them.
  //
  // The Plans tab's own dialog needs nothing here — it is a modal, and the
  // rail is behind its backdrop while it is open.
  // Setting the brand up and changing one already set up are the same form, and
  // the prompt says which is happening — the same split the Save row under it
  // makes, off the same answer. "Setup brand" put to somebody amending a brand
  // they finished months ago names something that is already done; "Save
  // changes" put to somebody who has never had one names changes to nothing.
  //
  // From `saved` rather than `details`, for the reason the strip above is: it
  // is the brand as stored that decides which of the two this is, not the boxes
  // as typed.
  // Only while the form is open: the screen beside it reads the brand back
  // and holds nothing of anyone's.
  useUnsavedChanges(
    editing && dirty,
    isBrandSetUp(saved)
      ? {
          title: "Unsaved brand details",
          body: "You’ve changed your brand details but haven’t saved them yet. Save your changes, or they’ll be discarded.",
          confirmLabel: "Save changes",
        }
      : {
          title: "Unsaved brand details",
          body: "You’ve started setting up your brand but haven’t saved it yet. Finish setting up your brand, or these details will be discarded.",
          confirmLabel: "Setup brand",
        },
    // The Save row's own answer, handed to the prompt so its button does what
    // it says. `commit` is called through an arrow rather than passed: it is
    // declared below, and the literal is built as this renders.
    // `discard` is the Cancel button's answer, handed up for the tab switch:
    // the page stays mounted across one, so a draft let go of has to be put
    // back to the saved copy here or it would still be in the boxes.
    {
      canSave: valid && !saving,
      save: () => commit(),
      report: () => problems.current?.(),
      discard: () => setDetails(saved),
    }
  );

  const handleChange = (patch: Partial<BrandDetails>) =>
    setDetails((current) => ({ ...current, ...patch }));

  // The Guidelines & policy tab's two documents, held here for the reason
  // `details` is: the tab is unmounted by a switch to another one, and what
  // was being written has to survive the trip. The stored copies are the
  // hook's; the drafts open on them and Cancel goes back to them.
  const toast = useToast();
  const {
    stored: storedPolicies,
    status: policiesStatus,
    reload: reloadPolicies,
    publish: publishPolicy,
    reset: resetPolicy,
  } = useBrandPolicies();
  const policiesPhase = useLoadPhase(policiesStatus === "loading");
  const [policies, setPolicies] = useState<PolicyDocuments>({
    guidelines: "",
    policy: "",
  });
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [resettingPolicies, setResettingPolicies] = useState(false);

  // Seeded the way `details` is - during the render that first sees a read
  // land, and once per document rather than once for the pair: a publish
  // of the guidelines alone hands back a new guidelines record, and the
  // policy draft beside it must not be put back to its stored copy for
  // that. `stored[id]` is a fresh object only when a read or a write of
  // that document answered, so an ordinary re-render seeds nothing.
  const [policiesSeededFrom, setPoliciesSeededFrom] = useState<
    Partial<StoredPolicyDocuments>
  >({});
  if (storedPolicies) {
    const landed = POLICY_DOCUMENT_IDS.filter(
      (id) => storedPolicies[id] !== policiesSeededFrom[id]
    );
    if (landed.length > 0) {
      setPoliciesSeededFrom(storedPolicies);
      setPolicies((current) => {
        const next = { ...current };
        landed.forEach((id) => {
          next[id] = storedPolicies[id].body;
        });
        return next;
      });
    }
  }

  const policiesDirty = storedPolicies
    ? POLICY_DOCUMENT_IDS.some(
        (id) => !sameRichText(policies[id], storedPolicies[id].body)
      )
    : false;

  const reportPolicyFailure = (title: string, reason: unknown) => {
    console.error(title, reason);
    toast.error(
      title,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );
  };

  /**
   * One Save row, but two documents and two endpoints: whichever of the two
   * is off its stored copy is published, the two together, and each is
   * reported on its own - the way the Details form's two records are. A
   * policy refused for its clauses is no reason to lose guidelines that
   * saved. What comes back is what the box shows from then on, since the
   * server keeps only what is on its allow-list.
   */
  const commitPolicies = async (): Promise<boolean> => {
    if (!storedPolicies) return false;
    const changed = POLICY_DOCUMENT_IDS.filter(
      (id) => !sameRichText(policies[id], storedPolicies[id].body)
    );
    if (changed.length === 0) return true;

    setSavingPolicies(true);
    try {
      const outcomes = await Promise.allSettled(
        changed.map((id) => publishPolicy(id, policies[id]))
      );
      const failed = changed.filter(
        (_, index) => outcomes[index].status === "rejected"
      );
      outcomes.forEach((outcome, index) => {
        if (outcome.status === "rejected") {
          reportPolicyFailure(
            `Could not save your ${POLICY_DOCUMENT_NAMES[changed[index]].toLowerCase()}`,
            outcome.reason
          );
        }
      });

      const saved = changed.filter((id) => !failed.includes(id));
      if (saved.length > 0) {
        toast.success(
          saved.length === 2
            ? "Guidelines & policy saved"
            : `${POLICY_DOCUMENT_NAMES[saved[0]]} saved`,
          "They go on every plan created from now on."
        );
      }
      return failed.length === 0;
    } finally {
      setSavingPolicies(false);
    }
  };

  /**
   * Back to the system default, on the server, right away - a reset is not
   * a draft change waiting on Save. The hook writes what came back through
   * the cache, and the seeding above puts it in the box.
   */
  const handleResetPolicy = async (id: PolicyDocumentId) => {
    setResettingPolicies(true);
    try {
      await resetPolicy(id);
      toast.success(
        `${POLICY_DOCUMENT_NAMES[id]} reset`,
        "Back to the default Naurist text, for every plan created from now on."
      );
    } catch (reason) {
      reportPolicyFailure(
        `Could not reset your ${POLICY_DOCUMENT_NAMES[id].toLowerCase()}`,
        reason
      );
    } finally {
      setResettingPolicies(false);
    }
  };

  /**
   * A draft back on its stored copy — one document for the tab's Cancel,
   * which closes the one editor it belongs to, and both for the rail's
   * Discard, which is throwing the whole screen's typing away.
   */
  const revertPolicies = (id?: PolicyDocumentId) => {
    if (!storedPolicies) return;
    const reverting = id ? [id] : POLICY_DOCUMENT_IDS;
    setPolicies((current) => {
      const next = { ...current };
      reverting.forEach((one) => {
        next[one] = storedPolicies[one].body;
      });
      return next;
    });
  };

  // The second thing on this page that can be left unsaved, registered on
  // its own: the shell asks about whichever is dirty, in that one's words,
  // and about both in turn where both are.
  useUnsavedChanges(
    policiesDirty,
    {
      title: "Unsaved guidelines & policy",
      body: "You’ve changed your guidelines or policy but haven’t saved them yet. Save your changes, or they’ll be discarded.",
      confirmLabel: "Save changes",
    },
    {
      canSave: !savingPolicies,
      save: () => commitPolicies(),
      report: () => {},
      // Through an arrow: called bare it would be handed the prompt's own
      // argument as the document to put back, and revert only that one.
      discard: () => revertPolicies(),
    }
  );

  /**
   * The form is one screen but two records, so a save is two PATCHes sent
   * together — and either can fail on its own.
   *
   * What came back is written into `details` as well as `saved`: the backend
   * normalises as it stores, and the form has to show what was actually kept
   * rather than what was typed. A half that failed is committed to neither, so
   * it stays dirty, keeps the user's typing, and is what the next Save retries.
   *
   * Answers with whether the whole form landed, which is what the rail's prompt
   * needs to know before it lets a navigation through — a half-stored brand is
   * still something to lose, and leaving the screen would lose it. The message
   * naming the half that failed is the hook's — see `useBrandSave`.
   */
  const commit = async (): Promise<boolean> => {
    const { profile, payment } = await save(details);
    if (!profile && !payment) return false;

    const landed = { ...profile, ...payment };
    setDetails((current) => ({ ...current, ...landed }));
    setSaved((current) => ({ ...current, ...landed }));

    const stored = !!profile && !!payment;

    // Back to the brand read back, but only once the whole of it landed: a
    // half-stored form still holds something of the user's, and closing it
    // over their typing is how it would be lost.
    if (stored) setEditing(false);

    return stored;
  };

  const handleSave = () => {
    void commit();
  };

  // Back to what was last stored, and out of the form — the same two halves
  // Cancel has meant on this screen and on Settings.
  const handleCancel = () => {
    setDetails(saved);
    setEditing(false);
  };

  // Whether the fields are what the Details column is holding, as against the
  // brand being read back. `editing` is not the whole answer: a brand nobody
  // has stored keeps the fields up whatever it says, because there is no
  // read-back screen worth showing for one — it would be an empty shape with
  // an Edit button, saying nothing and asking for nothing.
  const detailsFormOpen =
    status === "ready" && (editing || !isBrandSetUp(saved));

  return (
    // From `lg` up the page owns the viewport height instead of growing past
    // it, so the shell never gets a page-level scrollbar — only the form
    // column scrolls. Below `lg` the columns stack and the page scrolls
    // normally, which is the right behaviour on a phone.
    <div className="lg:h-full lg:flex lg:flex-col lg:overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="My brand setup" />

      <div className="mt-5 flex-shrink-0 border-b border-line">
        <Tabs tabs={BRAND_TABS} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* Worked out from the saved record's own fields — nothing is asked of
          the backend for it — and from `saved` rather than `details`, so it is
          the brand as stored that answers rather than the boxes as typed. A
          form filled in but not yet saved still carries the strip, and the
          response to Save is what takes it away: `handleSave` writes what came
          back into `saved`, so the strip goes at the moment the record behind
          the form is complete, not at the moment the last field is.

          Its shape stands in while the read is out, on the same phase as the
          two columns below, so the strip and the form land together instead of
          the page filling in from the top down. A load that failed knows
          nothing to report at all, and shows neither.

          The details tab only. What it asks for is on the form under it — a
          name, contact details, bank details — so on the Plans tab it would
          be a demand with nothing on screen to meet it. */}
      {activeTab === "details" &&
        (status === "loading" ? (
          <SetupBannerSkeleton phase={phase} />
        ) : status === "ready" && !isBrandSetUp(saved) ? (
          <SetupBanner title="Set up your brand to get started" />
        ) : null)}

      {activeTab === "details" ? (
        // The preview column is sized by the document rather than fixed, so
        // however large the page ends up there is no leftover strip of empty
        // column between it and the form.
        <div className="mt-5 lg:flex-1 lg:min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-5 3xl:gap-6">
          {/* The column hands its height to the form, which scrolls its own
              sections and keeps the Save row pinned below them. Its overlay
              scrollbar stays hidden until hovered or scrolled. */}
          <div className="min-h-0 lg:h-full">
            {/* The form only opens once there is something to open it with:
                painting empty fields and filling them a moment later reads as
                the screen overwriting what was typed into it. Its own shape
                stands in the meantime, so the column is the right size before
                and after. */}
            {status === "loading" ? (
              <BrandDetailsFormSkeleton phase={phase} />
            ) : status === "failed" ? (
              <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-md border border-line bg-white">
                {/* Its own description rather than the generic one, and truer:
                    this screen only ever reads back what is already saved, so
                    a failure here cannot have cost anyone what they typed. */}
                <EmptyState
                  {...failedEmptyState(
                    "We could not load your brand details",
                    <Button size="sm" variant="secondary" onClick={reload}>
                      Try again
                    </Button>
                  )}
                  description="Nothing has been lost - this is only what is saved being read back."
                />
              </div>
            ) : !detailsFormOpen ? (
              // The brand as stored, and the way into the form.
              //
              // Only ever for a brand that is set up. The read-back screen is
              // the stored brand shown back, and on a brand nobody has stored
              // it is an empty shape with an Edit button — a screen that says
              // nothing and asks for nothing. So until the first save lands,
              // the fields are what this column holds, whatever `editing` says.
              // The form drops its Cancel to match, so there is no way in here
              // to begin with; this is the same rule kept where the screens are
              // chosen, so a later way out cannot reopen the empty one.
              // It scrolls its own cards from `lg` up, where the page has
              // locked its height and hidden its overflow: the form beside it
              // scrolls its sections for the same reason, and a read-back
              // screen that did not would have its payment QR cut off by the
              // bottom of a short window with no scrollbar left to reach it.
              <div className="lg:h-full lg:overflow-y-auto custom-scrollbar-overlay">
                <BrandOverview
                  details={saved}
                  onEdit={() => setEditing(true)}
                />
              </div>
            ) : (
              <BrandDetailsForm
                details={details}
                onChange={handleChange}
                errors={errors}
                onSave={handleSave}
                onCancel={handleCancel}
                dirty={dirty}
                saving={saving}
                // The same answer the strip above is drawn from, and from
                // `saved` for the same reason: it is the brand as stored that
                // decides whether this save sets it up or changes it, so the
                // button keeps its first-time wording until the record behind
                // the form is actually complete.
                setUp={isBrandSetUp(saved)}
                problems={problems}
              />
            )}
          </div>
          {/* Held back while the form is, on the same phase: the document is
              drawn from specimen values and could open the instant the page
              mounts, but one column arriving ahead of the other reads as the
              screen arriving twice. A load that fails is `ready` as far as
              this is concerned — the preview belongs beside the retry. */}
          <BrandPreviewPanel
            details={details}
            active={preview}
            onChange={setPreview}
            phase={phase}
          />
        </div>
      ) : activeTab === "plans" ? (
        // Same height contract as the details column: from `lg` up the card
        // fills what's left of the viewport and its table scrolls inside.
        <div className="mt-5 lg:flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden">
          <BrandPlansTab />
        </div>
      ) : activeTab === "services" ? (
        // Same height contract again: the card fills what is left of the
        // viewport and its grid of cards scrolls inside it.
        <div className="mt-5 lg:flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden">
          <BrandServicesTab />
        </div>
      ) : activeTab === "guidelines" ? (
        // Same height contract as the Form tab: from `lg` up the tab fills
        // what is left of the viewport, and the document scrolls inside its
        // own card. The tab only opens once both documents are in - a box
        // that fills itself a moment after it is painted reads as the screen
        // overwriting what was typed - and its shape stands in the meantime.
        //
        // The hint over it sits outside that contract, and outside the
        // loading states: it says what the tab is for, which is true
        // whether or not the documents have arrived, so it is on screen
        // from the first paint and stays put while the cards under it
        // change.
        <div className="mt-5 lg:flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden">
          <HintBanner
            storageKey="brand-guidelines-intro"
            title="Write it once - it rides along on every PDF you send"
            description="Set dietary guidelines and policies to auto-include in clients' meal plans, supplements, and exercise PDFs via the Guidelines and Policy tabs."
            className="mb-5"
          />
          {policiesStatus === "failed" ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-md border border-line bg-white">
              <EmptyState
                {...failedEmptyState(
                  "We could not load your guidelines and policy",
                  <Button size="sm" variant="secondary" onClick={reloadPolicies}>
                    Try again
                  </Button>
                )}
                description="Nothing has been lost - this is only what is saved being read back."
              />
            </div>
          ) : !storedPolicies ? (
            <SkeletonCard phase={policiesPhase} className="h-full min-h-[420px]" />
          ) : (
            <BrandPoliciesTab
              documents={policies}
              stored={storedPolicies}
              onChange={(patch) =>
                setPolicies((current) => ({ ...current, ...patch }))
              }
              onSave={commitPolicies}
              onCancel={(id) => revertPolicies(id)}
              onReset={(id) => void handleResetPolicy(id)}
              saving={savingPolicies}
              resetting={resettingPolicies}
            />
          )}
        </div>
      ) : activeTab === "configuration" ? (
        // How the plan builder behaves for this practice — the Dish search
        // setting its header switch flips, explained. As tall as its cards
        // and scrolling with the page: nothing in it is pinned.
        <div className="mt-5 lg:flex-1 lg:min-h-0 lg:overflow-y-auto custom-scrollbar-overlay">
          <BrandConfigurationTab />
        </div>
      ) : (
        // Same height contract as the other two: from `lg` up the tab fills
        // what is left of the viewport, which is what lets the phone preview
        // inside it be sized to fit rather than scrolled to.
        <div className="mt-5 lg:flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden">
          {/* `details` rather than `saved`, so a logo picked a moment ago is
              already in the preview — the same live copy the invoice beside
              the Details form is drawn from. */}
          {/* The two tabs a form with nothing to sell sends people to. The
              page owns which tab is open - it is in the URL - so the Form tab
              is handed the way there rather than reaching for the router. */}
          <BrandFormTab
            details={details}
            onCreatePlan={() => setActiveTab("plans")}
            onCreateService={() => setActiveTab("services")}
          />
        </div>
      )}
    </div>
  );
};

export default MyBrandPage;
